import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowDown, Calendar } from 'lucide-react';
import { contentAPI } from '../utils/api';
import { openReservation } from '../utils/reservations';

// The fallback URL intentionally matches the first slide returned by the
// API so that first-time visitors don't see a brief swap-flicker when the
// API responds with a different URL. The image is committed in the repo
// (/app/backend/uploads/) so it ships with the build.
const FALLBACK_SLIDES = [
  { image_url: '/api/upload/images/a8803eb1-7bf4-4fc9-be7e-cbff5088df77.gif', alt: 'Full spread of Brick Lane signature dishes', order: 1, is_active: true },
];
const FALLBACK_INTERVAL = 3000;
const FALLBACK_TRANSITION = 1500;
const CACHE_KEY = 'bl_hero_cache_v1';

const readCachedHero = () => {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && Array.isArray(parsed.slides) && parsed.slides.length > 0) return parsed;
  } catch { /* ignore */ }
  return null;
};

const writeCachedHero = (data) => {
  try { window.localStorage.setItem(CACHE_KEY, JSON.stringify(data)); }
  catch { /* ignore */ }
};

const Hero = () => {
  const navigate = useNavigate();
  // Returning visitors: hydrate from localStorage cache → real images on first paint.
  // First-time visitors: use FALLBACK_SLIDES, swap in API result when ready.
  const [hero, setHero] = useState(() => readCachedHero() || { slides: FALLBACK_SLIDES });
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    contentAPI.getHero().then((data) => {
      if (data && Array.isArray(data.slides) && data.slides.length > 0) {
        setHero(data);
        writeCachedHero(data);
      }
    });
  }, []);

  const slides = useMemo(() => {
    const list = (hero?.slides || FALLBACK_SLIDES).filter((s) => s && s.is_active !== false && s.image_url);
    return list.sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [hero]);

  const intervalMs = hero?.slide_interval_ms || FALLBACK_INTERVAL;
  const transitionMs = hero?.transition_duration_ms || FALLBACK_TRANSITION;

  useEffect(() => {
    if (slides.length <= 1) return undefined;
    const id = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % slides.length);
    }, intervalMs);
    return () => clearInterval(id);
  }, [slides.length, intervalMs]);

  const scrollToStory = () => {
    const element = document.getElementById('story');
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  const title = hero?.title || 'Brick Lane';
  const slogan = hero?.slogan || 'Los Angeles Soul. Indian Roots.';
  const location = hero?.location || 'Arts District, Los Angeles';

  return (
    <section
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#0a0a0a]"
      data-testid="hero-section"
    >
      <div className="absolute inset-0 z-0" data-testid="hero-slideshow">
        {slides.map((slide, idx) => (
          <img
            key={slide.image_url}
            src={slide.image_url}
            alt={slide.alt || ''}
            loading={idx === 0 ? 'eager' : 'lazy'}
            fetchpriority={idx === 0 ? 'high' : 'auto'}
            decoding="async"
            className={`absolute inset-0 w-full h-full object-cover ease-in-out ${
              idx === activeIndex ? 'opacity-100' : 'opacity-0'
            }`}
            style={{ transitionProperty: 'opacity', transitionDuration: `${transitionMs}ms` }}
            data-testid={`hero-slide-${idx}`}
          />
        ))}
      </div>

      {slides.length > 1 && (
        <div className="absolute bottom-0 left-0 right-0 z-10 h-px" data-testid="hero-slide-indicators">
          <div className="flex h-full">
            {slides.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setActiveIndex(idx)}
                aria-label={`Show slide ${idx + 1}`}
                data-testid={`hero-indicator-${idx}`}
                className={`flex-1 h-full transition-colors duration-500 ${
                  idx === activeIndex ? 'bg-white' : 'bg-white/15 hover:bg-white/40'
                }`}
              />
            ))}
          </div>
        </div>
      )}

      <div className="relative z-10 text-center px-6 max-w-4xl mx-auto" style={{ textShadow: '0 2px 12px rgba(0,0,0,0.45)' }}>
        <h1
          className="text-white mb-4"
          style={{
            fontFamily: 'var(--font-serif)',
            fontSize: 'clamp(3.5rem, 10vw, 6rem)',
            lineHeight: '1.1',
            letterSpacing: '0.02em',
          }}
        >
          {title}
        </h1>
        <p className="text-white text-3xl md:text-4xl mb-8 font-light tracking-wide" style={{ lineHeight: '1.6' }}>
          {slogan}
        </p>
        <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
          <button
            onClick={() => openReservation(null, 'hero')}
            className="text-white text-lg font-semibold hover:opacity-70 transition-opacity inline-flex items-center gap-2"
            data-testid="hero-reserve-btn"
          >
            <Calendar size={18} /> RESERVE
          </button>
          <span className="text-white/50 hidden sm:inline">|</span>
          <button
            onClick={() => navigate('/menu')}
            className="text-white text-lg font-semibold hover:opacity-70 transition-opacity"
            data-testid="hero-menu-btn"
          >
            Menu
          </button>
        </div>
      </div>

      <button
        onClick={scrollToStory}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 animate-bounce"
        aria-label="Scroll to story"
      >
        <ArrowDown className="text-white" size={32} />
      </button>
    </section>
  );
};

export default Hero;
